import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, ChevronDown } from 'lucide-react'
import { ROUTES } from '@/utils/constants'
import { useNavigationStore } from '@/stores/navigation'

const Header: React.FC = () => {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigationStore = useNavigationStore()

  // Menu structure matching the prototype
  const menu = {
    platform: [
      { label: t('navigation.serviceSummary'), href: ROUTES.SERVICE_SUMMARY },
      { label: t('products.referralcx'), href: ROUTES.PRODUCTS.REFERRAL_CX },
      { label: t('products.schedulingcx'), href: ROUTES.PRODUCTS.SCHEDULING_CX },
      { label: t('products.cellcx'), href: ROUTES.PRODUCTS.CELL_CX },
      { label: t('products.casecx'), href: ROUTES.PRODUCTS.CASE_CX },
      { label: t('products.econsultcx'), href: ROUTES.PRODUCTS.ECONSULT_CX },
      { label: t('products.probationcx'), href: ROUTES.PRODUCTS.PROBATION_CX },
      { label: t('products.vaxmanagercx'), href: ROUTES.PRODUCTS.VAX_MANAGER_CX },
      { label: t('products.consumercx'), href: ROUTES.PRODUCTS.CONSUMER_CX },      
      { label: t('navigation.technicalOverview'), href: ROUTES.TECHNICAL, separator: true },
    ],
    resources: [
      { label: t('navigation.useCaseFinder'), href: ROUTES.USE_CASE },
      { label: t('navigation.whitePapers'), href: ROUTES.WHITE_PAPERS },
      { label: t('navigation.roiCalculator'), href: ROUTES.ROI },
      { label: t('navigation.blog'), href: ROUTES.BLOG, separator: true },
    ],
    about: [
      { label: t('navigation.company'), href: ROUTES.ABOUT },
      //{ label: t('navigation.news'), href: ROUTES.NEWS },
    ],
  }

  // Dropdown component matching prototype
  const Dropdown: React.FC<{ label: string; items: any[]; mobile?: boolean }> = ({ label, items, mobile = false }) => {
    const [open, setOpen] = useState(false)
    const [hoverTimeout, setHoverTimeout] = useState<number | null>(null)

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout)
        }
      }
    }, [hoverTimeout])

    const handleMouseEnter = () => {
      if (!mobile) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout)
          setHoverTimeout(null)
        }
        setOpen(true)
      }
    }

    const handleMouseLeave = () => {
      if (!mobile) {
        const timeout = setTimeout(() => {
          setOpen(false)
        }, 150) // Small delay to allow moving to dropdown
        setHoverTimeout(timeout)
      }
    }

    const handleClick = () => {
      if (mobile) {
        setOpen((o) => !o)
      }
    }

    return (
      <div
        className={`relative ${mobile ? "w-full" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          onClick={handleClick}
          className={`flex w-full items-center ${mobile ? "justify-between" : "justify-start"} rounded-xl px-3 py-2 text-slate-900 hover:text-blue-600`}
        >
          <span className="text-lg">{label}</span>
          <ChevronDown className={`ml-1 h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div
            role="menu"
            className={`${mobile ? "w-full" : "absolute left-0 mt-2 w-64"} rounded-xl border border-slate-200 bg-white p-2 shadow-lg z-50`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ul className="divide-y divide-slate-100">
              {items.map((item, idx) => (
                <li key={idx} className="py-1 first:pt-0 last:pb-0">
                  {item.separator && <div className="my-2 h-px bg-slate-200" aria-hidden="true" />}
                  <Link
                    to={item.href}
                    className="block rounded-lg px-3 py-2 text-sm text-slate-900 hover:text-blue-600 hover:bg-slate-50"
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  }

  // Logo component matching prototype
  const Logo: React.FC = () => {
    const [logoError, setLogoError] = useState(false)
    return (
      <Link to="/" className="relative flex items-center gap-2 select-none h-12 w-auto">
        <div className="relative h-12 w-auto">
          {!logoError ? (
            <img
              src="/EceptionistLogoLinerWithR.png"
              alt="Eceptionist logo"
              className="h-12 w-auto object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className="text-2xl font-semibold tracking-tight text-slate-900">eceptionist</span>
          )}
        </div>        
      </Link>
    )
  }

  return (
    <header id="navigation" className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/90 backdrop-blur" role="banner">
      <div className="flex items-center justify-between py-4 px-4 lg:px-6">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" role="navigation" aria-label="Main navigation">
          <Dropdown label={t('navigation.platform')} items={menu.platform} />
          <Link 
            to={ROUTES.AI} 
            className="rounded-xl px-3 py-2 text-lg text-slate-900 hover:text-blue-600 whitespace-nowrap"
          >
            {t('navigation.aiInEceptionist')}
          </Link>
          <Link 
            to={ROUTES.WHO_WE_SERVE} 
            className="rounded-xl px-3 py-2 text-lg text-slate-900 hover:text-blue-600 whitespace-nowrap"
          >
            {t('navigation.whoWeServe')}
          </Link>
          <Dropdown label={t('navigation.resources')} items={menu.resources} />
          <Dropdown label={t('navigation.about')} items={menu.about} />
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:block">
          <button
            onClick={navigationStore.openDemoModal}
            className="inline-flex items-center justify-center px-4 py-2 lg:px-6 lg:py-3 rounded-xl bg-[#1F2B59] text-white font-semibold shadow-lg hover:shadow-xl hover:bg-[#18244C] transition text-sm lg:text-base whitespace-nowrap"
          >
            {t('common.bookDemo')}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden rounded p-2 hover:bg-slate-100"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden space-y-2 border-t border-slate-200 bg-white p-4">
          <Dropdown label={t('navigation.platform')} items={menu.platform} mobile />
          <Link 
            to={ROUTES.AI} 
            className="block rounded-lg px-3 py-2 text-lg text-slate-900 hover:text-blue-600"
          >
            {t('navigation.aiInEceptionist')}
          </Link>
          <Link 
            to={ROUTES.WHO_WE_SERVE} 
            className="block rounded-lg px-3 py-2 text-lg text-slate-900 hover:text-blue-600"
          >
            {t('navigation.whoWeServe')}
          </Link>
          <Dropdown label={t('navigation.resources')} items={menu.resources} mobile />
          <Dropdown label={t('navigation.about')} items={menu.about} mobile />
          <button
            onClick={navigationStore.openDemoModal}
            className="mt-3 inline-flex items-center justify-center px-6 py-3 rounded-xl bg-[#1F2B59] text-white font-semibold shadow-lg hover:shadow-xl hover:bg-[#18244C] transition"
          >
            {t('common.bookDemo')}
          </button>
        </div>
      )}
    </header>
  )
}

export default Header


